import React, { useEffect } from "react";
import PortonLogoImg from "../img/Porton_Default.png";

// ─── Inline icons — same pattern as LoginPage.js ──────────────────────────────
const FacebookIcon = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
		<path d="M14 9h2.5V6h-2.5c-2 0-3.5 1.5-3.5 3.5V11H8v3h2.5v7h3v-7h2.3l.4-3h-2.7V9.8c0-.5.3-.8.8-.8z" />
	</svg>
);

const XIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" width="15" height="15" aria-hidden="true">
		<path d="M5 5l14 14M19 5L5 19" />
	</svg>
);

const YoutubeIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="17" height="17" aria-hidden="true">
		<rect x="2.5" y="6" width="19" height="12" rx="3" />
		<polygon points="10,9.3 10,14.7 15,12" fill="currentColor" stroke="none" />
	</svg>
);

const InstagramIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="16" height="16" aria-hidden="true">
		<rect x="3.5" y="3.5" width="17" height="17" rx="5" />
		<circle cx="12" cy="12" r="4" />
		<circle cx="17" cy="7" r="0.8" fill="currentColor" stroke="none" />
	</svg>
);

const LinkedinIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="16" height="16" aria-hidden="true">
		<rect x="3.5" y="3.5" width="17" height="17" rx="3" />
		<line x1="7.5" y1="10.5" x2="7.5" y2="17" />
		<circle cx="7.5" cy="7" r="0.6" fill="currentColor" stroke="none" />
		<path d="M11 17v-4.3c0-1 .3-2.2 2-2.2s2 1.2 2 2.2V17" />
		<line x1="11" y1="11" x2="11" y2="17" />
	</svg>
);

// ─── Inline styles — exact same pattern and values as LoginPage.js footer ─────
const injectFooterStyles = () => {
	if (document.getElementById("report-footer-styles")) return;
	const style = document.createElement("style");
	style.id = "report-footer-styles";
	style.textContent = `
		.rf-footer {
			background: #F7F2EE;
			border-top: 1px solid rgba(0,0,0,0.04);
			width: 100%;
			padding: 32px 0 22px;
			margin-top: auto;
			box-sizing: border-box;
			font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
		}
		.rf-footer *, .rf-footer *::before, .rf-footer *::after {
			box-sizing: border-box;
		}
		.rf-footer__inner {
			max-width: 1200px;
			margin: 0 auto;
			padding: 0 16px;
			display: flex;
			flex-direction: column;
			align-items: center;
		}
		.rf-footer__logo {
			max-height: 50px;
			max-width: 120px;
			object-fit: contain;
			opacity: 0.75;
			margin-bottom: 16px;
			display: block;
		}
		.rf-footer__links {
			display: flex;
			align-items: center;
			gap: 22px;
			flex-wrap: wrap;
			justify-content: center;
			margin-bottom: 18px;
			list-style: none;
			padding: 0;
		}
		.rf-footer__links a {
			font-size: 12px;
			color: #6B6760;
			text-decoration: none;
		}
		.rf-footer__links a:hover { color: #DD5E22; }
		.rf-footer__divider {
			width: 100%;
			max-width: 400px;
			height: 1px;
			background: rgba(0,0,0,0.08);
			margin-bottom: 18px;
		}
		.rf-footer__socials {
			display: flex;
			align-items: center;
			gap: 10px;
			margin-bottom: 18px;
		}
		.rf-footer__social-icon {
			width: 34px;
			height: 34px;
			border: 1px solid rgba(24,24,20,0.10);
			border-radius: 8px;
			color: #DD5E22;
			background: #fff;
			display: flex;
			align-items: center;
			justify-content: center;
			text-decoration: none;
			transition: background 0.15s ease, border-color 0.15s ease;
		}
		.rf-footer__social-icon:hover {
			background: #FCEEE5;
			border-color: #F5D5C0;
		}
		.rf-footer__copyright {
			font-size: 11px;
			color: #999999;
			margin: 0;
		}
	`;
	document.head.appendChild(style);
};

// ─── Component ─────────────────────────────────────────────────────────────────
export default function ReportFooter() {
	useEffect(() => {
		injectFooterStyles();
	}, []);

	return (
		<footer className="rf-footer">
			<div className="rf-footer__inner">
				<img
					className="rf-footer__logo"
					src={PortonLogoImg}
					alt="Porton Health"
				/>

				<nav aria-label="Footer links">
					<ul className="rf-footer__links">
						<li>
							<a href="https://www.portonhealth.com/terms-and-conditions/">
								Terms of service
							</a>
						</li>
						<li>
							<a href="https://www.portonhealth.com/faq/">FAQ</a>
						</li>
						<li>
							<a href="https://www.portonhealth.com/privacy-and-security/">
								Privacy &amp; security
							</a>
						</li>
					</ul>
				</nav>

				<div className="rf-footer__divider" />

				<div className="rf-footer__socials">
					<a
						className="rf-footer__social-icon"
						href="https://www.facebook.com/people/Porton-Health/100049262388140"
						aria-label="Facebook"
					>
						<FacebookIcon />
					</a>
					<a
						className="rf-footer__social-icon"
						href="https://x.com/PortonHealth"
						aria-label="Twitter / X"
					>
						<XIcon />
					</a>
					<a
						className="rf-footer__social-icon"
						href="https://www.youtube.com/channel/UCZJ3tfkrBJIESr0A94vkRYg"
						aria-label="YouTube"
					>
						<YoutubeIcon />
					</a>
					<a
						className="rf-footer__social-icon"
						href="https://www.instagram.com/portonhealth/?hl=en"
						aria-label="Instagram"
					>
						<InstagramIcon />
					</a>
					<a
						className="rf-footer__social-icon"
						href="https://www.linkedin.com/company/porton-health/posts/?feedView=all"
						aria-label="LinkedIn"
					>
						<LinkedinIcon />
					</a>
				</div>

				<p className="rf-footer__copyright">
					© {new Date().getFullYear()} Porton Health
				</p>
			</div>
		</footer>
	);
}